import { type InlineColumnResponse } from '@blaze-shared/datasets-data-access';
import { type DatasetColumn } from '@blaze-shared/schema-domain';

/**
 * Checks if the given column is a readonly link column
 * by checking if its formula contains 'TRUE()'.
 * This is used to identify if the column is a link type.
 *
 * @param column The dataset column to check
 * @returns Boolean indicating if the column is a readonly link
 */
export function isInlineColumnReadonlyLink(column: Partial<DatasetColumn>): boolean {
  return column?.formula?.includes('TRUE()') ?? false;
}

/**
 * Finds a linked inline column by its ID from the given columns array.
 * If isLink is provided, it will filter based on the link type as well.
 *
 * @param columns Array of dataset columns to search in
 * @param columnId ID of the column to find
 * @param isLink Optional boolean to filter by link type
 * @returns The found column or undefined if not found
 */
export function findLinkedInlineColumnById(
  columns: DatasetColumn[],
  columnId: string,
  isLink?: boolean,
): DatasetColumn | undefined {
  if (isLink === undefined) {
    return columns.find((column) => column.id === columnId);
  } else {
    return columns.find(
      (column) => column.id === columnId && isLink === isInlineColumnReadonlyLink(column),
    );
  }
}

/**
 * Finds a linked column that matches the given inline column
 * by checking if the column's formula includes the inline column's ID
 * and if the link type matches.
 * @param columns Array of dataset columns to search in
 * @param column The inline column to find a match for
 * @returns The found linked column or undefined if not found
 */
export function getLinkedColumnMatch(
  column: InlineColumnResponse,
  columns: DatasetColumn[],
): DatasetColumn | undefined {
  return columns.find(
    (oc) => oc.formula?.includes(column.id) && column.isLink === oc.formula?.includes('TRUE()'),
  );
}

/**
 * Checks if the linked columns have not changed
 * by comparing the old and new columns based on their formula and link type
 *
 * @param oldColumns Array of existing dataset columns
 * @param newColumns Array of new inline columns to compare against
 * @returns Boolean indicating if the linked columns have not changed
 */
export function checkIfLinkedColumnsNotChanged(
  oldColumns: DatasetColumn[] = [],
  newColumns: InlineColumnResponse[] | undefined = [],
): boolean {
  return (
    !!newColumns?.every((nc) =>
      oldColumns.find(
        (oc) =>
          nc.id && oc.formula?.includes(nc.id) && nc.isLink === isInlineColumnReadonlyLink(oc),
      ),
    ) && newColumns.length === oldColumns.length
  );
}

/**
 * Filters out columns that are not present in the new columns
 * based on their formula and link type
 *
 * @param oldColumns Array of existing dataset columns
 * @param newColumns Array of new inline columns to compare against
 * @returns Filtered array of columns to be removed
 */
export function getColumnsToRemove(
  oldColumns: DatasetColumn[],
  newColumns: InlineColumnResponse[] | undefined,
): DatasetColumn[] {
  return oldColumns.filter(
    (oc) =>
      !newColumns?.find(
        (nc) => oc.formula?.includes(nc.id) && nc.isLink === isInlineColumnReadonlyLink(oc),
      ),
  );
}

/**
 * Filters out columns that are not present in the old columns
 * based on their formula and link type
 *
 * @param oldColumns Array of existing dataset columns
 * @param newColumns Array of new inline columns to compare against
 * @returns Filtered array of new inline columns to be added
 */
export function getColumnsToAdd(
  oldColumns: DatasetColumn[],
  newColumns: InlineColumnResponse[] | undefined,
): InlineColumnResponse[] {
  return (
    newColumns?.filter(
      (nc) =>
        !oldColumns.find(
          (oc) => oc.formula?.includes(nc.id) && nc.isLink === isInlineColumnReadonlyLink(oc),
        ),
    ) ?? []
  );
}

/**
 * Finds matching inline columns between dataset columns and existing columns
 * based on ID matching or formula relationship
 *
 * @param datasetInlineColumns Array of columns from the dataset
 * @param inlineColumns Array of existing or new columns to match against
 * @returns Filtered array of matching dataset columns
 */
export function findMatchingInlineColumns(
  datasetInlineColumns: (InlineColumnResponse & { icon: string; selected: boolean })[] | undefined,
  inlineColumns: Partial<DatasetColumn>[],
): (InlineColumnResponse & { icon: string; selected: boolean })[] | undefined {
  if (!datasetInlineColumns) {
    return undefined;
  }

  return datasetInlineColumns.filter((datasetInlineColumn) =>
    inlineColumns.find((existingOrNew) => {
      // Match by ID and link type
      const isIdMatch =
        existingOrNew.id === datasetInlineColumn.id &&
        'isLink' in existingOrNew &&
        datasetInlineColumn.isLink === existingOrNew.isLink;

      // Match by formula inclusion and compatible link types
      const isFormulaMatch =
        datasetInlineColumn.isLink === isInlineColumnReadonlyLink(existingOrNew) &&
        existingOrNew.formula?.includes(datasetInlineColumn.id);

      return isIdMatch || isFormulaMatch;
    }),
  );
}
